/**
 * @file fetchBillFile.ts
 * @description Fetches a public bill JSON file and adapts it into a
 * `DistrictDict` keyed by district identifiers.
 *
 * Expected public file path:
 *   /public/data/congress{congressId}/bills/{billId}.json
 * Example:
 *   /public/data/congress118/bills/hconres3.json
 */

import { asset } from '@/utils/asset';
import { parseBillToDistrictDict } from './parseBill';
import type { DistrictDict } from '@/types';

type RawDistrict = {
  voteCast?: string;
  state?: string;
  district?: number;
  name?: string;
  bioguideId?: string;
  partyName?: string;
  member_link?: string;
  img_url?: string;
  img_attribution?: string;
};

type BillMetadata = {
  description?: string;
  title?: string;
  recommended?: string[];
  [key: string]: unknown;
};

type BillJson = {
  metadata?: BillMetadata;
  data?: Record<string, RawDistrict>;
};

function adaptBillFile(
  billJson: unknown,
  ctx: { congressId: number; billId: string },
): {
  billId: string;
  congressId: number;
  metadata: BillMetadata | undefined;
  data: Record<string, RawDistrict>;
} {
  const json = billJson as BillJson;
  return {
    billId: ctx.billId,
    congressId: ctx.congressId,
    metadata: json?.metadata,
    data: json?.data ?? {},
  };
}

export type BillFileResult = {
  districts: DistrictDict;
  metadata: { description?: string; recommendedMetricKeys?: string[] } | null;
};

export async function fetchBillFile(congressId: number, billId: string): Promise<BillFileResult> {
  const url = asset(`data/congress${congressId}/bills/${billId}.json`);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`[fetchBillFile] HTTP ${res.status} for ${url}`);

  const json: unknown = await res.json();
  const bill = adaptBillFile(json, { congressId, billId });

  const districts = parseBillToDistrictDict(bill.data, congressId);

  // Extract recommended metric keys (always a string array)
  const recommendedMetricKeys = bill.metadata?.recommended;

  const metadata = bill.metadata
    ? {
        description: bill.metadata.description,
        ...(recommendedMetricKeys && recommendedMetricKeys.length > 0 && { recommendedMetricKeys }),
      }
    : null;

  return { districts, metadata };
}
